import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useCookie } from 'react-use';

import type { z, ZodTypeAny } from 'zod';

export function useSchemaCookie<T extends ZodTypeAny>(
	schema: T,
	key: string,
	defaultValue?: z.input<T>,
): [
	value: z.output<T> | undefined,
	setValue: (value: z.input<T> | ((currentValue: z.output<T> | undefined) => z.input<T>)) => void,
] {
	const [rawValue, setRawValue] = useCookie(key);
	const currentRawValue = useRef(rawValue);
	currentRawValue.current =
		rawValue ?? (defaultValue !== undefined && defaultValue !== null ? JSON.stringify(defaultValue) : null);

	const value = useMemo(() => {
		try {
			return (schema.safeParse(currentRawValue.current).data ?? undefined) as z.output<T> | undefined;
		} catch {
			return undefined;
		}
	}, [schema]);

	const setValue = useCallback(
		(value: z.input<T> | ((currentValue: z.output<T> | undefined) => z.input<T>)) => {
			const nextValue =
				value instanceof Function ?
					value((schema.safeParse(currentRawValue.current).data ?? undefined) as z.output<T> | undefined)
				:	value;

			setRawValue(JSON.stringify(nextValue));
		},
		[setRawValue, currentRawValue, schema],
	);

	return [value, setValue];
}

export function useLocalStorage<T>(
	key: string,
	defaultValue: T,
): [value: T, setValue: (value: T | ((currentValue: T) => T)) => void] {
	const [storedValue, setStoredValue] = useState<T>(() => {
		try {
			const item = localStorage.getItem(key);
			return item !== null ? (JSON.parse(item) as T) : defaultValue;
		} catch (error) {
			console.warn(`Error reading localStorage key "${key}":`, error);
			return defaultValue;
		}
	});

	const setValue = useCallback(
		(value: T | ((currentValue: T) => T)) => {
			try {
				const valueToStore = value instanceof Function ? value(storedValue) : value;
				setStoredValue(valueToStore);
				localStorage.setItem(key, JSON.stringify(valueToStore));
			} catch (error) {
				console.warn(`Error setting localStorage key "${key}":`, error);
			}
		},
		[key, storedValue],
	);

	useEffect(() => {
		const handleStorageChange = (e: StorageEvent) => {
			if (e.key === key && e.newValue !== null) {
				try {
					setStoredValue(JSON.parse(e.newValue) as T);
				} catch (error) {
					console.warn(`Error parsing localStorage key "${key}":`, error);
				}
			}
		};

		window.addEventListener('storage', handleStorageChange);
		return () => window.removeEventListener('storage', handleStorageChange);
	}, [key]);

	return [storedValue, setValue];
}

export function useSchemaLocalStorage<T extends ZodTypeAny>(
	schema: T,
	key: string,
	defaultValue: z.output<T>,
): [value: z.output<T>, setValue: (value: z.input<T> | null | ((currentValue: z.output<T>) => z.input<T>)) => void] {
	const [storedValue, setStoredValue] = useState<z.output<T>>(() => {
		try {
			const item = localStorage.getItem(key);
			if (item !== null) {
				const parsed = JSON.parse(item) as unknown;
				const result = schema.safeParse(parsed);
				return result.success ? (result.data as z.output<T>) : defaultValue;
			}
			return defaultValue;
		} catch (error) {
			console.warn(`Error reading localStorage key "${key}":`, error);
			return defaultValue;
		}
	});

	const setValue = useCallback(
		(value: null | z.input<T> | ((currentValue: z.output<T>) => z.input<T>)) => {
			try {
				const valueToStore = value instanceof Function ? value(storedValue) : value;
				if (valueToStore === null) {
					setStoredValue(defaultValue);
					localStorage.removeItem(key);
					return;
				}

				const validatedValue = schema.parse(valueToStore) as z.output<T>;
				setStoredValue(validatedValue);
				localStorage.setItem(key, JSON.stringify(validatedValue));
			} catch (error) {
				console.warn(`Error setting localStorage key "${key}":`, error);
			}
		},
		[key, storedValue, schema, defaultValue],
	);

	useEffect(() => {
		const handleStorageChange = (e: StorageEvent) => {
			if (e.key === key && e.newValue !== null) {
				try {
					const parsed = JSON.parse(e.newValue) as unknown;
					const result = schema.safeParse(parsed);
					if (result.success) {
						setStoredValue(result.data as z.output<T>);
					}
				} catch (error) {
					console.warn(`Error parsing localStorage key "${key}":`, error);
				}
			}
		};

		window.addEventListener('storage', handleStorageChange);
		return () => window.removeEventListener('storage', handleStorageChange);
	}, [key, schema]);

	return [storedValue, setValue];
}
